import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort, TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { importInvestorEntitiesQuickFilterOptions } from '../../../constants';
import { IEntityImportComparison, ImportInvestorEntitiesUpdateMethodOptionEnum, TypeWindow } from '../../../types';
import { columns } from '../constants';
import MobileTable from './mobileTable';
import TableBody from './tableBody';

interface TableProps {
  tableData: IEntityImportComparison[];
  updateMethod: ImportInvestorEntitiesUpdateMethodOptionEnum;
  openEntityImportDetailPage: (type: TypeWindow, row: IEntityImportComparison) => void;
  sort: ISort;
  setSort: Dispatch<SetStateAction<ISort>>;
  search: string;
  onChangeSearch: (value: string) => void;
  primaryFilter: string;
  changePrimaryFilterValue: (filter: string) => void;
}

const Table = ({
  tableData,
  updateMethod,
  openEntityImportDetailPage,
  sort,
  setSort,
  search,
  onChangeSearch,
  primaryFilter,
  changePrimaryFilterValue
}: TableProps) => {
  const { isMobile } = useResponsive();

  const [tableColumns, setTableColumns] = useState<TableColumns[]>([]);
  const [sortedTableData, setSortedTableData] = useState<IEntityImportComparison[]>([]);

  useEffect(() => {
    if (updateMethod === ImportInvestorEntitiesUpdateMethodOptionEnum.UpdateUsingEntityStaticID) {
      setTableColumns([{ title: 'Entity Static ID', key: 'ENTITY_STATIC_ID', width: 18, keySort: 'Entity STATIC ID' }, ...columns]);
    } else {
      setTableColumns([{ title: 'Entity CRM ID', key: 'ENTITY_CRM_ID', width: 18, keySort: 'entity CRM ID' }, ...columns]);
    }
  }, [updateMethod]);

  useEffect(() => {
    const sortedData = [...tableData].sort((a, b) => {
      switch (sort.key) {
        case 'ENTITY_STATIC_ID':
          return sort.asc ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id);
        case 'ENTITY_CRM_ID':
          return sort.asc ? a.entityCRMId.localeCompare(b.entityCRMId) : b.entityCRMId.localeCompare(a.entityCRMId);
        case 'ENTITY':
          return sort.asc ? a.entityName.localeCompare(b.entityName) : b.entityName.localeCompare(a.entityName);
        case 'HOUSEHOLD':
          return sort.asc ? a.householdName.localeCompare(b.householdName) : b.householdName.localeCompare(a.householdName);
        case 'FIELDS_MODIFIED':
          return sort.asc ? a.fieldsModifiedCount - b.fieldsModifiedCount : b.fieldsModifiedCount - a.fieldsModifiedCount;
        case 'STATUS':
          return sort.asc ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
        default:
          return 0;
      }
    });

    setSortedTableData(sortedData);
  }, [sort, tableData]);

  const filteredTableData = () => {
    const newTableData = [...sortedTableData];
    const searchLower = search.toLowerCase();
    const filteredTableData = newTableData.filter(
      (item) => (item.entityCRMId ?? '').toLowerCase().includes(searchLower) || (item.entityName ?? '').toLowerCase().includes(searchLower)
    );

    switch (primaryFilter) {
      case 'Updates Only':
        return filteredTableData.filter((item) => item.status !== 'error' && item.status !== 'duplicate' && item.status !== 'unchanged');
      case 'Errors Only':
        return filteredTableData.filter((item) => item.status === 'error' || item.status === 'duplicate');
      default:
        return filteredTableData;
    }
  };

  return (
    <>
      <FilterPage
        search={{
          value: search,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        quickFilter={{
          title: 'Show',
          value: primaryFilter,
          onChange: changePrimaryFilterValue,
          defaultValue: 'All Rows',
          options: importInvestorEntitiesQuickFilterOptions
        }}
        resultsValue={filteredTableData().length}
        useNewFilters
      />
      <TableWrapper padding="0">
        {isMobile ? (
          <MobileTable
            tableColumns={tableColumns}
            tableData={filteredTableData()}
            updateMethod={updateMethod}
            openEntityImportDetailPage={openEntityImportDetailPage}
          />
        ) : (
          <>
            <TableHeader refetch={() => null as any} columns={tableColumns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              <TableBody
                tableColumns={tableColumns}
                tableData={filteredTableData()}
                updateMethod={updateMethod}
                openEntityImportDetailPage={openEntityImportDetailPage}
              />
            </TableBodyWrapper>
          </>
        )}
      </TableWrapper>
    </>
  );
};

export default Table;
